import React, {FormEvent, useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useSelector} from 'react-redux';
import {User} from '../../incase-domain/user';
import {transformZodErrors} from '../../incase-domain/validators/transformZodErrors';
import {usernameValidator} from '../../incase-domain/validators/user/username';
import {useUpdateUser} from '../pages/auth/queries';
import {loggedInUserSelector} from '../../store/selectors/auth.selectors';
import ToggleSwitch from '../../incase-domain/components/toggleSwitch/ToggleSwitch';
import {useLogout} from './common/header/queries';
import Button from '../../incase-domain/components/button/Button';
import ConfirmModal from '../../incase-domain/components/modals/confirmModal/ConfirmModal';
import SettingsItem from '../../incase-domain/components/settings/SettingsItem';
import DebouncedTextInput from '../../components/slides/highlighted/common/debounced/DebouncedTextInput';
import Modal from '../../incase-domain/components/modals/directory/Modal';
import ChangePassword from './ChangePassword';
import {type TypedTranslation} from '../../i18n';

function UpdateUser(): JSX.Element {
    const {t} = useTranslation();
    const sessionsLogout = useLogout(true);
    const updateUserMutation = useUpdateUser();

    const user = useSelector(loggedInUserSelector);
    const [localErrors, setLocalErrors] = useState<Record<string, string[]> | undefined>({});

    const isUserUpdating = updateUserMutation.isLoading;
    const serverErrors = updateUserMutation.data?.success === false ? updateUserMutation.data?.errors : undefined;
    const errors = serverErrors ?? localErrors;

    const isAnonymous = user?.isAnonymous;

    const [username, setUsername] = useState(user?.username);
    const [sessionsLogoutModal, setSessionsLogoutModal] = useState<boolean>(false);
    const [updatePasswordModal, setUpdatePasswordModal] = useState<boolean>(false);
    const [updateUsernameModal, setUpdateUsernameModal] = useState<boolean>(false);

    useEffect(() => {
        setUsername(user?.username);
    }, [user?.username]);

    const updateUsername = (e: FormEvent): void => {
        e.preventDefault();
        if (!username) return;

        const validation = usernameValidator.safeParse(username);

        if (validation.success) {
            setLocalErrors({});
            updateUserMutation.mutate({user: {...user, username} as User, userConfig: {}});
        } else {
            const validationErrors = transformZodErrors(validation.error.errors);
            setLocalErrors({username: validationErrors?.undefined || []});
        }
    };

    return (
        <>
            <div>
                <SettingsItem
                    icon="solar:user-rounded-linear"
                    title={t('settingsPage.usernameUpdate')}
                    description={t('settingsPage.usernameUpdateDescription')}
                    trailing={
                        <Button size="xs" variant="soft" leading="solar:pen-2-linear" onClick={() => setUpdateUsernameModal(true)}>
                            {t('settingsPage.usernameUpdate')}
                        </Button>
                    }
                />

                <SettingsItem
                    icon="solar:password-linear"
                    title={t('settingsPage.passwordUpdate')}
                    description={t('settingsPage.passwordUpdateDescription')}
                    trailing={
                        <Button square size="xs" variant="soft" leading="solar:pen-2-linear" onClick={() => setUpdatePasswordModal(true)}>
                            {t('settingsPage.passwordUpdate')}
                        </Button>
                    }
                />

                <SettingsItem
                    icon="solar:lock-keyhole-minimalistic-linear"
                    title={t('settingsPage.changeAnonymity')}
                    description={t('settingsPage.changeAnonymityTooltip')}
                    trailing={
                        <ToggleSwitch
                            id="private-account"
                            isOn={!!isAnonymous}
                            handleToggle={() => {
                                if (user) {
                                    updateUserMutation.mutate({user: {...user, isAnonymous: !isAnonymous}, userConfig: {}});
                                }
                            }}
                        />
                    }
                />

                <SettingsItem
                    icon="solar:diskette-linear"
                    title={t('settingsPage.autoSave')}
                    description={t('settingsPage.autoSaveDescription')}
                    trailing={
                        <ToggleSwitch
                            id="auto-save"
                            isOn={!!user?.autoSave}
                            handleToggle={() => {
                                if (user) {
                                    updateUserMutation.mutate({user: {...user, autoSave: !user.autoSave}, userConfig: {}});
                                }
                            }}
                        />
                    }
                />

                <SettingsItem
                    icon="solar:logout-3-linear"
                    title={t('settingsPage.logOutFromSessions')}
                    description={t('settingsPage.logOutFromSessionsDescription')}
                    trailing={
                        <Button onClick={() => setSessionsLogoutModal(true)} size="sm" variant="soft" color="primary">
                            {t('sidebar.logOut')}
                        </Button>
                    }
                />
            </div>
            <ConfirmModal
                isOpen={sessionsLogoutModal}
                onCancel={() => setSessionsLogoutModal(false)}
                onConfirm={() => {
                    sessionsLogout();
                    setSessionsLogoutModal(false);
                }}
                cancelText={t('common.cancel')}
                showCancel
                showConfirm
                confirmText={t('common.confirm')}
                message={t('settingsPage.sessionsLogoutConfirmation')}
                color="warning"
            />
            <Modal
                isOpen={updatePasswordModal}
                icon="solar:lock-keyhole-bold"
                header={user?.username}
                description={t('settingsPage.passwordUpdate')}
                onCancel={() => {
                    setUpdatePasswordModal(false);
                }}
            >
                <ChangePassword />
            </Modal>
            <Modal
                isOpen={updateUsernameModal}
                icon="solar:pen-2-linear"
                header={user?.username}
                description={t('settingsPage.usernameUpdate')}
                onCancel={() => {
                    setUpdateUsernameModal(false);
                }}
                size="sm"
            >
                <form className="" onSubmit={updateUsername}>
                    <div className={`c-field ${errors?.username && 'is-invalid'}`}>
                        <div className="u-flex u-h-between u-gap-2">
                            <DebouncedTextInput
                                className="c-input"
                                data-cy="changeUsername"
                                onChange={(value) => {
                                    setUsername(value);
                                }}
                                value={username || ''}
                                placeholder={t('form.username')}
                            />

                            <Button
                                data-cy="btnChangeUsername"
                                size="xs"
                                color="secondary"
                                loading={isUserUpdating}
                                leading="solar:diskette-linear"
                                disabled={user?.username === username || !username || isUserUpdating}
                                type="submit"
                            >
                                {t('common.save')}
                            </Button>
                        </div>

                        {errors?.username?.map((err) => (
                            <div className="c-form-help is-invalid" key={err}>
                                {t(err as TypedTranslation)}
                            </div>
                        ))}
                    </div>
                </form>
            </Modal>
        </>
    );
}

export default UpdateUser;
